<template>
    <div id="ProfileForm" class="flex flex-col gap-8">
        <section
            class="relative grid grid-cols-1 gap-12 min-[950px]:grid-cols-2"
            :class="{ 'hidden': !isTopSectionVisible }"
        >
            <!-- Fields (left side) -->
            <div class="flex flex-col">
                <!-- LinkedIn Synchronization -->
                <LinkedInProfileButton
                    :class="{ 'hidden': onlyMissingFields }"
                    v-model:connection-data="localLinkedInConnectionData"
                    :disabled="disabled"
                    v-model:profile-data="localLinkedInProfileData"
                    v-model:state="localLinkedInState"
                />

                <CLineBreak v-if="!onlyMissingFields" class="mx-auto my-8 w-1/2" />

                <FirstName
                    v-if="show('firstName')"
                    :disabled="isFieldDisabled('firstName', localLinkedInConnectionData) || isDisabled"
                    :error="errors.firstName"
                    required
                    v-bind="synchronizedFieldProps('firstName', localLinkedInConnectionData)"
                    :model-value="profile.firstName"
                    @change="resolveSlug()"
                    @update:model-value="update('firstName', $event)"
                >
                    <template #side>
                        <LinkedInSynchronizeSwitch
                            v-model:connection-data="localLinkedInConnectionData"
                            :disabled="onlyMissingFields || isDisabled"
                            field="firstName"
                            :profile-data="localLinkedInProfileData"
                            :value="profile.firstName"
                            @update:model-value="update('firstName', $event)"
                        />
                    </template>
                    <template #footer>
                        <LinkedInValuePreview
                            class="mt-1.5"
                            :connection-data="localLinkedInConnectionData"
                            :disabled="onlyMissingFields || isDisabled"
                            field="firstName"
                            :profile-data="localLinkedInProfileData"
                            :value="profile.firstName"
                            @update:model-value="update('firstName', $event)"
                        />
                    </template>
                </FirstName>

                <!-- Priezvisko (lastName) -->
                <LastName
                    v-if="show('lastName')"
                    :disabled="isFieldDisabled('lastName', localLinkedInConnectionData) || isDisabled"
                    :error="errors.lastName"
                    required
                    v-bind="synchronizedFieldProps('lastName', localLinkedInConnectionData)"
                    :model-value="profile.lastName"
                    @change="resolveSlug()"
                    @update:model-value="update('lastName', $event)"
                >
                    <template #side>
                        <LinkedInSynchronizeSwitch
                            v-model:connection-data="localLinkedInConnectionData"
                            :disabled="onlyMissingFields || isDisabled"
                            field="lastName"
                            :profile-data="localLinkedInProfileData"
                            :value="profile.lastName"
                            @update:model-value="update('lastName', $event)"
                        />
                    </template>
                    <template #footer>
                        <LinkedInValuePreview
                            class="mt-1.5"
                            :connection-data="localLinkedInConnectionData"
                            :disabled="onlyMissingFields || isDisabled"
                            field="lastName"
                            :profile-data="localLinkedInProfileData"
                            :value="profile.lastName"
                            @update:model-value="update('lastName', $event)"
                        />
                    </template>
                </LastName>

                <!-- Trvalý odkaz (slug) -->
                <CFormGroup v-if="show('slug')">
                    <SlugInput
                        v-show="profile.slug"
                        ref="slugInput"
                        collection="profiles"
                        :disabled="isDisabled"
                        :exclude-id="profile.id"
                        path="..."
                        :model-value="profile.slug"
                        @update:model-value="update('slug', $event)"
                    />
                </CFormGroup>

                <LinkedInNoDomainDataAlert
                    class="mb-3"
                    :connection-data="localLinkedInConnectionData"
                    :disabled="isDisabled"
                    :domains="[EMemberSnapshotDataDomains.PROFILE]"
                    v-model:profile-data="localLinkedInProfileData"
                />

                <!-- Location (unified: geoCity, geoCountry, geoRegion) -->
                <CFormGroup
                    v-if="show('geoCountry') || show('geoCity')"
                    description="Kde momentálne žiješ alebo tráviš najviac času"
                    label="Kde žiješ?"
                    required
                >
                    <CLocationInput
                        :disabled="isDisabled"
                        :model-value="locationData"
                        @update:model-value="handleLocationUpdate"
                    />
                </CFormGroup>

                <LinkedInNoDomainDataAlert
                    class="mb-3"
                    :connection-data="localLinkedInConnectionData"
                    :disabled="isDisabled"
                    :domains="[EMemberSnapshotDataDomains.PROFILE, EMemberSnapshotDataDomains.RICH_MEDIA]"
                    v-model:profile-data="localLinkedInProfileData"
                />

                <!-- Profilový obrázok (profileImg) -->
                <CFormGroup v-if="show('profileImg')" class="mt-3" label="Profilový obrázok">
                    <CImageInput
                        crop
                        description
                        :disabled="isFieldDisabled('profileImg', localLinkedInConnectionData) || isDisabled"
                        :model-value="profile.profileImg"
                        @update:model-value="update('profileImg', $event)"
                    />
                    <template #side>
                        <LinkedInSynchronizeSwitch
                            v-model:connection-data="localLinkedInConnectionData"
                            :disabled="onlyMissingFields || isDisabled"
                            field="profileImg"
                            :profile-data="localLinkedInProfileData"
                            :value="profile.profileImg"
                            @input="update('profileImg', $event)"
                        />
                    </template>
                    <template #footer>
                        <LinkedInValuePreview
                            class="mt-1.5"
                            :connection-data="localLinkedInConnectionData"
                            :disabled="onlyMissingFields || isDisabled"
                            field="profileImg"
                            :profile-data="localLinkedInProfileData"
                            :value="profile.profileImg"
                            @input="update('profileImg', $event)"
                        />
                    </template>
                </CFormGroup>
            </div>

            <!-- Right side -->
            <div v-if="show('iPhoneFrame')" class="hidden min-[950px]:block">
                <!-- Phone profile preview-->
                <aside class="sticky top-40 isolate mx-auto block w-full max-w-[450px] lg:top-28">
                    <div class="relative flex aspect-[0.5/1] flex-col items-center justify-center p-2">
                        <img
                            alt="iphone frame"
                            class="pointer-events-none absolute inset-0 z-10 h-full w-auto"
                            src="@/assets/images/iphone.png"
                        />

                        <div class="no-scrollbar size-full overflow-y-scroll rounded-[50px] bg-gray-100 dark:bg-subtle p-8 pl-7 pt-18">
                            <UserProfileView
                                class="no-scrollbar box-border overflow-y-scroll bg-gray-100 dark:bg-subtle"
                                minified
                                preview
                                :profile="modelValue"
                                :profile-view-type="profileViewType"
                            />
                        </div>
                    </div>
                </aside>
            </div>
        </section>

        <CLineBreak v-if="showAll" />

        <div class="flex flex-col">
            <!-- Najdôležitejšie kľúčové slová o mne (interests) -->
            <section v-if="show('interests')" class="mb-5">
                <CFormGroup
                    class="mb-2!"
                    description="Čomu sa venuješ, akú máš expertízu/špecializáciu, aké oblasti/odvetvia ťa zaujímajú."
                    :error="errors.interests"
                    label="Najdôležitejšie kľúčové slová o mne"
                    required
                >
                    <CAlert class="mb-2" icon="info" variant="info">
                        Jeden #hashtag (kľúčové slovo) môže obsahovať max. 3 slová (po 35 znakov). Hashtagy sa oddeľujú
                        enterom. Pomocou nich dokáže systém vyhľadávať a odporúčať.
                    </CAlert>

                    <CInterestsInput
                        :disabled="isDisabled"
                        placeholder="Pridaj kľúčové slová ..."
                        :model-value="profile.interests"
                        @update:model-value="update('interests', $event)"
                    />
                </CFormGroup>

                <SuggestedKeywords
                    :disabled="isDisabled"
                    :label="`${$t('jobForm.fields.topics.suggestion', lang)}:`"
                    :selected-keywords="profile.interests"
                    :suggested-keywords="suggestedKeywords.keywords"
                    @select-keyword="addTopic($event)"
                />
            </section>

            <LinkedInNoDomainDataAlert
                :connection-data="localLinkedInConnectionData"
                :disabled="isDisabled"
                :domains="[EMemberSnapshotDataDomains.PROFILE]"
                v-model:profile-data="localLinkedInProfileData"
            />

            <!-- Viac o mne (hobby) -->
            <CFormGroup
                v-if="show('hobby')"
                description="Čo by určite mali o tebe ostatní vedieť a nevyčítajú z tvojho životopisu."
                :error="errors.hobby"
                label="Viac o mne"
                required
            >
                <CWysiwyg
                    class="custom-height-wysiwyg"
                    :disabled="isFieldDisabled('hobby', localLinkedInConnectionData) || isDisabled"
                    :model-value="profile.hobby"
                    @update:model-value="update('hobby', $event)"
                />
                <template #side>
                    <LinkedInSynchronizeSwitch
                        v-model:connection-data="localLinkedInConnectionData"
                        :disabled="onlyMissingFields || isDisabled"
                        field="hobby"
                        :profile-data="localLinkedInProfileData"
                        :value="profile.hobby"
                        @update:model-value="update('hobby', $event)"
                    />
                </template>
                <template #footer>
                    <LinkedInValuePreview
                        class="mt-1.5"
                        :connection-data="localLinkedInConnectionData"
                        :disabled="onlyMissingFields || isDisabled"
                        field="hobby"
                        :profile-data="localLinkedInProfileData"
                        :value="profile.hobby"
                        @update:model-value="update('hobby', $event)"
                    />
                </template>
            </CFormGroup>

            <!-- Krátky popis (shortProfile) -->
            <CFormGroup v-if="show('shortProfile')" :error="errors.shortProfile" label="Krátky popis">
                <CWysiwyg
                    :allowed-buttons="['back', 'forward', 'bold', 'italic', 'strike']"
                    :disabled="isDisabled"
                    :placeholder="'Čo chceš vďaka tomu dosiahnuť, zmeniť, …'"
                    :model-value="profile.shortProfile"
                    @update:model-value="update('shortProfile', $event)"
                />
            </CFormGroup>
        </div>

        <CLineBreak v-if="showAll" />

        <!-- Vzdelanie (highSchools, universities) -->
        <section v-if="show('highSchools') || show('universities')" class="flex max-w-xl flex-col gap-6">
            <header class="flex">
                <CHeading class="mb-0!" variant="2">Vzdelanie</CHeading>
            </header>

            <LinkedInNoDomainDataAlert
                :connection-data="localLinkedInConnectionData"
                :disabled="isDisabled"
                :domains="[EMemberSnapshotDataDomains.EDUCATION]"
                v-model:profile-data="localLinkedInProfileData"
            />

            <!-- Stredné školy (highSchools) -->
            <div class="flex flex-col gap-8">
                <div v-if="show('highSchools')" class="flex flex-col gap-8">
                    <header class="flex items-center justify-between">
                        <CHeading class="mb-0!" variant="3">Stredné školy</CHeading>

                        <LinkedInSynchronizeSwitch
                            v-model:connection-data="localLinkedInConnectionData"
                            :disabled="onlyMissingFields || isDisabled"
                            field="highSchools"
                            :profile-data="localLinkedInProfileData"
                            :value="profile.highSchools"
                            @input="update('highSchools', $event)"
                        />
                    </header>

                    <EducationEdit
                        :disabled="isFieldDisabled('highSchools', localLinkedInConnectionData) || isDisabled"
                        is-draggable
                        v-bind="synchronizedFieldProps('highSchools', localLinkedInConnectionData)"
                        secondary
                        :model-value="profile.highSchools"
                        @update:model-value="update('highSchools', $event)"
                    />
                </div>

                <CLineBreak v-if="show('highSchools') && show('universities')" />

                <!-- Vysoké školy (universities) -->
                <div v-if="show('universities')" class="flex flex-col gap-8">
                    <header class="flex items-center justify-between">
                        <CHeading class="mb-0!" variant="3">Vysoké školy</CHeading>

                        <LinkedInSynchronizeSwitch
                            v-model:connection-data="localLinkedInConnectionData"
                            :disabled="onlyMissingFields || isDisabled"
                            field="universities"
                            :profile-data="localLinkedInProfileData"
                            :value="profile.universities"
                            @input="update('universities', $event)"
                        />
                    </header>

                    <EducationEdit
                        :disabled="isFieldDisabled('universities', localLinkedInConnectionData) || isDisabled"
                        is-draggable
                        v-bind="synchronizedFieldProps('universities', localLinkedInConnectionData)"
                        :model-value="profile.universities"
                        @update:model-value="update('universities', $event)"
                    />
                </div>
            </div>
        </section>

        <CLineBreak v-if="showAll" />

        <!-- Profesionálne skúsenosti (jobs)  -->
        <section v-if="show('jobs')" class="flex max-w-xl flex-col gap-4">
            <header class="flex items-baseline justify-between">
                <div class="flex flex-col">
                    <CHeading class="mb-0!" variant="3">Profesionálne skúsenosti</CHeading>
                    <span>(práca, podnikanie, dobrovoľníctvo)</span>
                </div>

                <LinkedInSynchronizeSwitch
                    v-model:connection-data="localLinkedInConnectionData"
                    :disabled="onlyMissingFields || isDisabled"
                    field="jobs"
                    :profile-data="localLinkedInProfileData"
                    :value="profile.jobs"
                    @input="update('jobs', $event)"
                />
            </header>

            <div class="mb-4 text-smaller">
                Skúsenosť, ktorú dáš najvyššie, sa bude zobrazovať aj v náhľade tvojho profilu vo formáte
                <strong>Názov pozície @ Spoločnosť</strong>.
            </div>

            <LinkedInNoDomainDataAlert
                :connection-data="localLinkedInConnectionData"
                :disabled="isDisabled"
                :domains="[EMemberSnapshotDataDomains.POSITIONS, EMemberSnapshotDataDomains.ORGANIZATIONS]"
                v-model:profile-data="localLinkedInProfileData"
            />

            <JobExperienceEdit
                :disabled="isFieldDisabled('jobs', localLinkedInConnectionData) || isDisabled"
                is-draggable
                :model-value="profileJobs"
                v-bind="synchronizedFieldProps('jobs', localLinkedInConnectionData)"
                @update:model-value="update('jobs', $event)"
            />
        </section>

        <CLineBreak v-if="showAll" />

        <!-- Jazykové znalosti (languages) -->
        <section v-if="show('languages')" class="flex max-w-xl flex-col gap-6">
            <header class="flex items-baseline justify-between">
                <div class="flex items-center gap-2">
                    <CHeading class="mb-0!" variant="3">Jazykové znalosti</CHeading>
                </div>

                <LinkedInSynchronizeSwitch
                    v-model:connection-data="localLinkedInConnectionData"
                    :disabled="onlyMissingFields || isDisabled"
                    field="languages"
                    :profile-data="localLinkedInProfileData"
                    :value="profile.languages"
                    @input="update('languages', $event)"
                />
            </header>

            <LinkedInNoDomainDataAlert
                :connection-data="localLinkedInConnectionData"
                :disabled="isDisabled"
                :domains="[EMemberSnapshotDataDomains.LANGUAGES]"
                v-model:profile-data="localLinkedInProfileData"
            />

            <LanguageEdit
                :disabled="isFieldDisabled('languages', localLinkedInConnectionData) || isDisabled"
                is-draggable
                :value="profile.languages || []"
                v-bind="synchronizedFieldProps('languages', localLinkedInConnectionData)"
                @update:model-value="update('languages', $event)"
            />
        </section>

        <CLineBreak v-if="showAll" />

        <!-- Doplnkové aktivity a úspechy (achievements) -->
        <section v-if="show('achievements')" class="flex max-w-xl flex-col gap-6">
            <header class="flex items-baseline justify-between">
                <div class="flex items-center gap-2">
                    <CHeading class="mb-0!" variant="3">Doplnkové aktivity a úspechy</CHeading>
                    <CTooltip>
                        Aktivity, ktorým sa venuješ nad rámec vzdelania či práce a úspechy, ktoré sa Ti podarili
                        dosiahnuť.
                    </CTooltip>
                </div>

                <LinkedInSynchronizeSwitch
                    v-model:connection-data="localLinkedInConnectionData"
                    :disabled="onlyMissingFields || isDisabled"
                    field="achievements"
                    :profile-data="localLinkedInProfileData"
                    :value="profile.achievements"
                    @input="update('achievements', $event)"
                />
            </header>

            <LinkedInNoDomainDataAlert
                :connection-data="localLinkedInConnectionData"
                :disabled="isDisabled"
                :domains="[
                    EMemberSnapshotDataDomains.PROJECTS,
                    EMemberSnapshotDataDomains.PUBLICATIONS,
                    EMemberSnapshotDataDomains.HONORS
                ]"
                v-model:profile-data="localLinkedInProfileData"
            />

            <AchievementEdit
                :disabled="isFieldDisabled('achievements', localLinkedInConnectionData) || isDisabled"
                is-draggable
                :model-value="profileAchievements"
                v-bind="synchronizedFieldProps('achievements', localLinkedInConnectionData)"
                @update:model-value="update('achievements', $event)"
            />
        </section>

        <!-- Hobby, záujmy (hobbiesAndInterests)  -->
        <CFormGroup v-if="show('hobbiesAndInterests')" :error="errors.hobbiesAndInterests">
            <CHeading variant="3">Hobby, záujmy</CHeading>

            <CWysiwyg
                class="custom-height-wysiwyg"
                :disabled="isDisabled"
                :model-value="profile.hobbiesAndInterests"
                @update:model-value="update('hobbiesAndInterests', $event)"
            />
        </CFormGroup>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, reactive } from 'vue'
import type { PropType } from 'vue'
import type { ValidationErrors } from 'last-validator'
import cloneDeep from 'lodash-es/cloneDeep'

import { EntityType } from '#shared/types/EntityType'
import {
    EMemberSnapshotDataDomains,
    type TLinkedinConnection,
    type TLinkedInMapperData
} from '#shared/types/LinkedIn'
import type { LoadingState } from '#shared/types/LoadingState'
import type { Achievement, JobPosition, UserProfile, UserProfileType } from '#shared/types/User'
import { addArrayItem, scrollToError } from '#shared/utils'
import { isFieldDisabled, synchronizedFieldProps } from '#shared/utils/linkedIn'
import SuggestedKeywordsUtils from '@/utils/suggestedKeywords'
import { validateUserProfile } from '@/validators/userProfile'
import { useSlug } from '@/composables/slug/useSlug'
import { useUser } from '@/composables/user/useUser'

type LinkedInProfileData = {
    profile: TLinkedInMapperData['profile']
    domains: EMemberSnapshotDataDomains[]
}

type EditableUserProfile = UserProfile & {
    geoCoordinates?: { lat: number; lng: number } | null
}

const props = defineProps({
    modelValue: {
        type: Object as PropType<UserProfile>,
        required: true
    },
    lang: {
        type: String as PropType<'sk' | 'en'>,
        default: 'sk'
    },
    disabled: {
        type: Boolean,
        default: false
    },
    onlyMissingFields: {
        type: Boolean,
        default: false
    },
    missingFields: {
        type: Array as PropType<string[]>,
        default: () => []
    },
    missFields: {
        type: Array as PropType<string[]>,
        default: undefined
    },
    linkedInConnectionData: {
        type: Object as PropType<TLinkedinConnection | null>,
        default: null
    },
    linkedInProfileData: {
        type: Object as PropType<LinkedInProfileData | null>,
        default: null
    },
    linkedInState: {
        type: String as PropType<LoadingState>,
        default: 'idle'
    }
})

const emit = defineEmits([
    'update:modelValue',
    'update:linkedInConnectionData',
    'update:linkedInProfileData',
    'update:linkedInState'
])

const applicationStore = useApplicationStore()
const userProfile = computed(() => applicationStore.userProfile || null)
const { resolveSlug: resolveUniqueSlug } = useSlug()
const { updateUserProfile } = useUser()

// Refs and reactive state
const slugInput = ref<any>(null)
const canSuggestSlug = ref(true)
const errors = reactive<ValidationErrors>({})
const profileViewType = ref<UserProfileType>(EntityType.UserProfile)
const profile = reactive<EditableUserProfile>({ ...props.modelValue })

// LinkedIn data
const localLinkedInConnectionData = ref(props.linkedInConnectionData)
const localLinkedInProfileData = ref(props.linkedInProfileData)
const localLinkedInState = ref(props.linkedInState)

// Suggested keywords
const suggestedKeywords = computed(() => {
    if (!userProfile.value) {
        // Return a mock object with empty arrays when userProfile is not available
        return {
            keywords: [],
            featuredKeywords: [],
            resolveFeatured: () => {}
        }
    }
    return new SuggestedKeywordsUtils('profileForm', userProfile.value)
})

// Computed properties
const isDisabled = computed(() => {
    return props.disabled || localLinkedInState.value === 'loading'
})

const showAll = computed(() => {
    return !props.onlyMissingFields
})

const isTopSectionVisible = computed(() => {
    return (
        show('firstName') ||
        show('lastName') ||
        show('slug') ||
        show('geoCountry') ||
        show('geoRegion') ||
        show('geoCity') ||
        show('profileImg')
    )
})

const profileJobs = computed<JobPosition[]>(() => profile.jobs || [])
const profileAchievements = computed<Achievement[]>(() => profile.achievements || [])

// Location data for CLocationInput
const locationData = computed(() => ({
    geoCity: profile.geoCity || '',
    geoCountry: profile.geoCountry || '',
    geoRegion: profile.geoRegion || null,
    geoCoordinates: profile.geoCoordinates || null
}))

// Methods
function show(field: string): boolean {
    if (props.missFields && props.missFields.includes(field)) return false
    return showAll.value || props.missingFields.includes(field)
}

function resolveSlug() {
    if (profile.firstName && profile.lastName && canSuggestSlug.value && slugInput.value) {
        slugInput.value.suggestSlug(`${profile.firstName}-${profile.lastName}`)
    }
}

// Enhanced function to resolve unique slug and save to Firestore
async function resolveAndSaveSlug() {
    if (!profile.firstName || !profile.lastName) return

    try {
        const fullName = `${profile.firstName} ${profile.lastName}`
        const uniqueSlug = await resolveUniqueSlug('profiles', fullName, profile.id)

        // Update the profile locally
        profile.slug = uniqueSlug
        emit('update:modelValue', { ...profile })

        // Save to Firestore if we have a user ID
        if (profile.id) {
            await updateUserProfile(profile.id, { slug: uniqueSlug })
            console.log('Slug saved to Firestore:', uniqueSlug)
            console.log(userProfile.value?.id)
        }

        // Update the slug input component
        if (slugInput.value) {
            slugInput.value.suggestSlug(uniqueSlug)
        }

        canSuggestSlug.value = false
    } catch (error) {
        console.error('Failed to resolve and save slug:', error)
    }
}

function update(property: string, value: any) {
    if (property === 'hobbiesAndInterests') {
        console.log('ProfileForm hobbiesAndInterests update called with:', value)
    }

    if (property === 'geoCountry' && value !== 'Slovensko') {
        profile.geoRegion = null
    }

    ;(profile as any)[property] = value
    emit('update:modelValue', { ...profile })

    if (property === 'slug') canSuggestSlug.value = false
}

// Handle location update from CLocationInput
function handleLocationUpdate(locationData: {
    geoCity: string
    geoCountry: string
    geoRegion: string | null
    geoCoordinates?: { lat: number; lng: number } | null
}) {
    profile.geoCity = locationData.geoCity
    profile.geoCountry = locationData.geoCountry
    profile.geoRegion = locationData.geoRegion
    if (locationData.geoCoordinates) {
        profile.geoCoordinates = locationData.geoCoordinates
    }
    emit('update:modelValue', { ...profile })
}

async function validate() {
    const result = await validateUserProfile(profile)

    Object.assign(errors, result.errors)

    if (!result.isValid) {
        nextTick(() => scrollToError())
    }

    return result
}

function addTopic(interest: string) {
    const interests = addArrayItem(profile.interests, interest)
    update('interests', interests)
}

// Watchers
watch(
    localLinkedInConnectionData,
    (newValue) => {
        emit('update:linkedInConnectionData', newValue)
    },
    { deep: true }
)

watch(
    localLinkedInProfileData,
    (newValue) => {
        emit('update:linkedInProfileData', newValue)
    },
    { deep: true }
)

watch(localLinkedInState, (newValue) => {
    emit('update:linkedInState', newValue)
})

watch(
    () => props.modelValue,
    (newValue) => {
        Object.assign(profile, cloneDeep(newValue))
    },
    { deep: true }
)

// Watch for firstName and lastName changes to resolve slug
watch(
    [() => profile.firstName, () => profile.lastName],
    ([newFirstName, newLastName], [oldFirstName, oldLastName]) => {
        // Only trigger if both names are present and at least one has changed
        if (newFirstName && newLastName && (newFirstName !== oldFirstName || newLastName !== oldLastName)) {
            // Use nextTick to ensure the profile updates are complete before resolving slug
            nextTick(() => {
                resolveAndSaveSlug()
            })
        }
    },
    { immediate: false }
)

defineExpose({
    validate,
    profile
})
</script>
